'use strict';

var jsxRuntime = require('react/jsx-runtime');
var isNumber = require('lodash/isNumber');
var react = require('react');
var reactTransitionGroup = require('react-transition-group');
var utilities_string = require('../../utilities/string.js');
var hooks_useCombinedRefs = require('../../hooks/useCombinedRefs.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var isNumber__default = /*#__PURE__*/_interopDefault(isNumber);

/** This function sets the transitional and then final styles on an element in a quick succession.
 * This is useful for animating an element from e.g `display: none` by resetting the display prop
 * in the transitional style and then setting e.g an opacity in the final style.
 */
const setTransitionalStyle = ({ element, transitionalStyle, finalStyle, }) => {
    for (const [key, value] of Object.entries(typeof transitionalStyle === 'function'
        ? transitionalStyle()
        : transitionalStyle)) {
        element.style.setProperty(utilities_string.kebabCase(key), // the property name needs to be in kebab-case https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleDeclaration/setProperty#propertyname
        value);
    }
    requestAnimationFrame(() => {
        // in some cases (particularly in firefox) requestAnimationFrame might execute _before_ the next paint, but we need to wait for
        // the starting size to be painted before we can set the final size. Nesting another requestAnimationFrame gives enough time for a repaint.
        // https://github.com/whatwg/html/issues/2569
        requestAnimationFrame(() => {
            for (const [key, value] of Object.entries(typeof finalStyle === 'function' ? finalStyle() : finalStyle)) {
                element.style.setProperty(utilities_string.kebabCase(key), value);
            }
        });
    });
};

const DEFAULT_TIMEOUT = 300;
const Collapsible = react.forwardRef(({ visible = false, direction = 'vertical', children, collapsedSize = '0', timeout = DEFAULT_TIMEOUT, appear = false, onEntering, onEntered, onExiting, onExited, unmountOnExit, testId, style, className, ...props }, ref) => {
    const internalRef = react.useRef(null);
    const combinedRefs = hooks_useCombinedRefs.useCombinedRefs(ref, internalRef);
    const isHiddenWhenCollapsed = collapsedSize === '0';
    const isHorizontal = direction === 'horizontal';
    const getTransitionDelay = (state) => {
        if (isNumber__default.default(timeout)) {
            return timeout;
        }
        const isEntering = state === 'entering' || state === 'entered';
        return timeout[isEntering ? 'enter' : 'exit'] ?? DEFAULT_TIMEOUT;
    };
    return (jsxRuntime.jsx(reactTransitionGroup.Transition, { appear: appear, in: visible, timeout: timeout, nodeRef: internalRef, unmountOnExit: unmountOnExit, onEntering: (...args) => {
            // since display:none is set in the exited state which stops all animations, we need to
            // first repaint with transient (or baseline) styles, and only then apply the final animation styles
            const element = internalRef.current;
            if (element) {
                setTransitionalStyle({
                    element,
                    transitionalStyle: {
                        [isHorizontal ? 'maxWidth' : 'maxHeight']: collapsedSize,
                        ...(isHiddenWhenCollapsed && {
                            opacity: '0',
                            overflow: 'hidden',
                        }),
                    },
                    finalStyle: () => {
                        // reading styles causes a reflow so we do it in a callback.
                        // this way it only causes a reflow just before the transition starts and not earlier
                        const scrollSize = `${isHorizontal ? element.scrollWidth : element.scrollHeight}px`;
                        return {
                            [isHorizontal ? 'maxWidth' : 'maxHeight']: scrollSize,
                            ...(isHiddenWhenCollapsed && {
                                opacity: '1',
                                overflow: 'hidden',
                            }),
                        };
                    },
                });
            }
            onEntering?.(...args);
        }, onEntered: onEntered, onExiting: (...args) => {
            const element = internalRef.current;
            if (element) {
                setTransitionalStyle({
                    element,
                    transitionalStyle: () => {
                        // reading styles causes a reflow so we do it in a callback.
                        // this way it only causes a reflow just before the transition starts and not earlier
                        const scrollSize = `${isHorizontal ? element.scrollWidth : element.scrollHeight}px`;
                        return {
                            [isHorizontal ? 'maxWidth' : 'maxHeight']: scrollSize,
                            ...(isHiddenWhenCollapsed && {
                                opacity: '1',
                                overflow: 'hidden',
                            }),
                        };
                    },
                    finalStyle: {
                        [isHorizontal ? 'maxWidth' : 'maxHeight']: collapsedSize,
                        ...(isHiddenWhenCollapsed && {
                            opacity: '0',
                            overflow: 'hidden',
                        }),
                    },
                });
            }
            onExiting?.(...args);
        }, onExited: onExited, children: (state) => (jsxRuntime.jsx("div", { style: {
                transition: `all ${getTransitionDelay(state)}ms ease-in-out`,
                // the entered and exited styles should be set here rather than in their respective callbacks
                // as the callbacks are not called on mount
                ...(state === 'entered' && {
                    [isHorizontal ? 'maxWidth' : 'maxHeight']: '',
                    ...(isHiddenWhenCollapsed && {
                        opacity: 1,
                        overflow: 'visible',
                    }),
                }),
                ...(state === 'exited' && {
                    [isHorizontal ? 'maxWidth' : 'maxHeight']: collapsedSize,
                    ...(isHiddenWhenCollapsed && {
                        opacity: 0,
                        // the collapsed content needs to be removed from the document flow otherwise it still counts
                        // towards the parent's scroll height. https://github.com/w3c/csswg-drafts/issues/4791
                        display: 'none',
                    }),
                }),
                ...(!isHiddenWhenCollapsed && { overflow: 'auto' }),
                ...style,
            }, inert: isHiddenWhenCollapsed &&
                ['exited', 'exiting'].includes(state)
                ? 'true'
                : undefined, "aria-hidden": isHiddenWhenCollapsed &&
                ['exited', 'exiting'].includes(state), className: className, "data-transitioning": 
            // used in tests so we can determine if an element has finished transitioning
            ['entering', 'exiting'].includes(state), "data-testid": testId, ...props, ref: combinedRefs, children: children })) }));
});
Collapsible.displayName = 'Collapsible';

exports.Collapsible = Collapsible;
//# sourceMappingURL=Collapsible.js.map
